import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, Fragment as _Fragment, createElementBlock as _createElementBlock, unref as _unref, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "offered-page" }
const _hoisted_2 = { class: "filters" }
const _hoisted_3 = { class: "table__footer" }
const _hoisted_4 = {
  key: 1,
  class: "empty"
}

import { computed, ref } from "vue";
import { useQuery, useMutation } from "@vue/apollo-composable";
import { useToast } from "vue-toastification";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";

import { type PaginatedResult } from "@/helpers/typesDefinition";
import { type Offered } from "@/helpers/project/typesDefinition";
import { formatPrice } from "@/helpers/utils";
import { isCFO } from "@/helpers/router/queries";

import TableBase, { type TableBaseColumn } from "@/components/TableBase.vue";
import BasicButton from "@/components/BasicButton.vue";
import OfferedDetails from "@/components/offered/OfferedDetails.vue";

import { GET_USER } from "@/graphql/user/queries";
import { GET_ALL_OFFERED } from "@/graphql/offered/queries";
import { SET_OFFERED_VALIDATION } from "@/graphql/offered/mutations";

type GetAllOfferedResult = {
  offered: PaginatedResult<Offered>;
};


export default /*@__PURE__*/_defineComponent({
  __name: 'OfferedAmountsToValidate',
  setup(__props) {

const { t } = useI18n({
  inheritLocale: true,
  useScope: "global",
});

const toast = useToast();

const { result: userResult } = useQuery(GET_USER);

const user = computed(() => userResult.value?.user);
const isUserCFO = computed(() => isCFO(user.value?.accessRights));

const invoicedFilter = ref<"invoiced" | "uninvoiced" | null>("uninvoiced");
const validatedFilter = ref<"validated" | "notValidated" | null>(
  "notValidated",
);

const invoicedFilterOptions = {
  invoiced: "Invoiced",
  uninvoiced: "Uninvoiced",
};

const validatedFilterOptions = {
  validated: "Validated",
  notValidated: "Not validated",
};

const queryVariables = computed(() => ({
  offset: 0,
  invoiced:
    invoicedFilter.value === null
      ? undefined
      : invoicedFilter.value === "invoiced",
  validated:
    validatedFilter.value === null
      ? undefined
      : validatedFilter.value === "validated",
}));

const { result, fetchMore } = useQuery<GetAllOfferedResult>(
  GET_ALL_OFFERED,
  queryVariables,
);

const refetchQueries = computed(() => [
  {
    query: GET_ALL_OFFERED,
    variables: queryVariables.value,
  },
]);

const rawOffered = computed(() => result.value?.offered.items ?? []);
const hasMore = computed(() => result.value?.offered.hasMore ?? false);

const columns: TableBaseColumn[] = [
  {
    name: "project",
    title: "Project",
  },
  {
    name: "amountNet",
    title: t("labels.offered.amountNet"),
  },
  {
    name: "date",
    title: t("labels.offered.date"),
  },
  {
    name: "comment",
    title: t("labels.offered.comment"),
  },
  {
    name: "status",
    title: "Status",
  },
];

const formattedOffered = computed(() =>
  rawOffered.value.map((item) => ({
    ...item,
    project: item.project?.name ?? "-",
    amountNet: formatPrice(item.amountNet),
    date: dayjs(item.date).format("DD/MM/YYYY"),
    comment: item.comment || "-",
    status: item.validated ? "Validated" : "Pending",
    invoice: item.invoice ?? null,
    creditNoteFor: item.creditNoteFor ?? null,
    validated: item.validated,
  })),
);

const modalOfferedId = ref("");

const isModalDetailsVisible = ref(false);

function selectOffered(offeredId: string) {
  modalOfferedId.value = offeredId;
}

function showModalDetails() {
  isModalDetailsVisible.value = true;
}

function closeOfferedDetailsModal() {
  isModalDetailsVisible.value = false;
}

const fullSelectedOffered = computed<Offered | null>(() => {
  return (
    rawOffered.value.find((offered) => offered.id === modalOfferedId.value) ??
    null
  );
});

const { mutate: setOfferedValidation } = useMutation(SET_OFFERED_VALIDATION);

function canUnvalidate(offered: Partial<Offered>) {
  return (
    offered.validated &&
    !offered.creditNoteFor &&
    (!offered.invoice ||
      ["draft", "canceled"].includes(offered.invoice.invoiceStatus))
    // remark: creditNoteFor menu already includes only non-draft invoices,
    // so we don't do the check for now, but it might be good to uniformize later.
  );
}

async function setValidation(
  offered: Partial<Offered> & { id: string },
  validated: boolean,
  event: Event,
) {
  event.stopPropagation();

  if (!isUserCFO.value) {
    toast.error("You are not allowed to validate offered amounts");
    return;
  }

  if (!validated && !canUnvalidate(offered)) {
    toast.error("Could not unvalidate invoiced offering");
    return;
  }

  try {
    const result = await setOfferedValidation(
      {
        id: offered.id,
        validated,
      },
      {
        refetchQueries: refetchQueries.value,
      },
    );

    const response = result?.data?.setOfferedValidation;

    if (response?.status === "ok") {
      toast.success(validated ? "Offering validated" : "Offering unvalidated");
      return;
    }

    toast.error(
      response?.error ||
        (validated
          ? "Could not validate offering"
          : "Could not unvalidate offering"),
    );
  } catch {
    toast.error(
      validated
        ? "Could not validate offering"
        : "Could not unvalidate offering",
    );
  }
}

function loadMore() {
  if (!hasMore.value) return;

  fetchMore({
    variables: {
      ...queryVariables.value,
      offset: rawOffered.value.length,
    },
    updateQuery: (previousResult, { fetchMoreResult }) => {
      if (!fetchMoreResult) {
        return previousResult;
      }

      return {
        offered: {
          ...fetchMoreResult.offered,
          items: [
            ...previousResult.offered.items,
            ...fetchMoreResult.offered.items,
          ],
        },
      };
    },
  });
}

return (_ctx: any,_cache: any) => {
  const _component_Multiselect = _resolveComponent("Multiselect")!

  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_component_Multiselect, {
        modelValue: invoicedFilter.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((invoicedFilter).value = $event)),
        placeholder: "Invoice status",
        title: "Filter by invoice status",
        options: invoicedFilterOptions,
        closeOnSelect: true,
        canClear: true,
        disabled: false
      }, null, 8, ["modelValue"]),
      _createVNode(_component_Multiselect, {
        modelValue: validatedFilter.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((validatedFilter).value = $event)),
        placeholder: "Validation status",
        title: "Filter by validation status",
        options: validatedFilterOptions,
        closeOnSelect: true,
        canClear: true,
        disabled: false
      }, null, 8, ["modelValue"])
    ]),
    _createVNode(TableBase, {
      columns: columns,
      items: formattedOffered.value,
      "is-clickable": true,
      onSelectItem: selectOffered,
      onClick: showModalDetails
    }, {
      actions: _withCtx(({ item: offered }) => [
        (isUserCFO.value)
          ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
              (!offered.validated)
                ? (_openBlock(), _createBlock(BasicButton, {
                    key: 0,
                    color: "success",
                    onClick: (event) => setValidation(offered, true, event)
                  }, {
                    default: _withCtx(() => [...(_cache[2] || (_cache[2] = [
                      _createTextVNode(" Validate ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["onClick"]))
                : (_openBlock(), _createBlock(BasicButton, {
                    key: 1,
                    color: "alert",
                    disabled: !canUnvalidate(offered),
                    onClick: (event) => setValidation(offered, false, event),
                    title: 
              canUnvalidate(offered)
                ? 'Unvalidate'
                : 'Cannot unvalidate an invoiced offering'
            
                  }, {
                    default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                      _createTextVNode(" Unvalidate ", -1)
                    ]))]),
                    _: 1
                  }, 8, ["disabled", "onClick", "title"]))
            ], 64))
          : _createCommentVNode("", true)
      ]),
      _: 1
    }, 8, ["items"]),
    _createElementVNode("div", _hoisted_3, [
      (hasMore.value)
        ? (_openBlock(), _createBlock(BasicButton, {
            key: 0,
            onClick: loadMore
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(_unref(t)("buttons.offered.loadMore")), 1)
            ]),
            _: 1
          }))
        : (_openBlock(), _createElementBlock("p", _hoisted_4, _toDisplayString(_unref(t)("offered.allLoaded")), 1))
    ]),
    (isModalDetailsVisible.value && fullSelectedOffered.value)
      ? (_openBlock(), _createBlock(OfferedDetails, {
          key: 0,
          id: modalOfferedId.value,
          project: fullSelectedOffered.value.project,
          offered: fullSelectedOffered.value,
          "has-client-access": false,
          hasAdminAccess: isUserCFO.value,
          refetchQueries: refetchQueries.value,
          onClose: closeOfferedDetailsModal
        }, null, 8, ["id", "project", "offered", "hasAdminAccess", "refetchQueries"]))
      : _createCommentVNode("", true)
  ]))
}
}

})